import React, { useState, useEffect } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import symptomService from '../services/symptomService';

const FirstCheckInBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [hasEntries, setHasEntries] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkFirstCheckInStatus();
    // Check if banner was dismissed
    const dismissedFlag = localStorage.getItem('firstCheckInBannerDismissed');
    if (dismissedFlag === 'true') {
      setDismissed(true);
    }
  }, [user]);

  const checkFirstCheckInStatus = async () => {
    // Don't show banner if user is not authenticated
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await symptomService?.getEntries();
      if (!error && data && data?.length > 0) {
        setHasEntries(true);
      } else {
        setHasEntries(false);
      }
    } catch (error) {
      console.error('Error checking first check-in status:', error);
      setHasEntries(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('firstCheckInBannerDismissed', 'true');
  };

  const handleStartCheckIn = () => {
    navigate('/daily-check-in');
  };

  // Don't show if:
  // - Loading
  // - User is not authenticated (not signed in yet)
  // - Banner was dismissed
  // - User already has check-in entries
  // - Already on daily-check-in page
  // - On email verification confirmation page (user hasn't verified email yet)
  if (
    loading || 
    !user || 
    dismissed || 
    hasEntries || 
    location?.pathname === '/daily-check-in' ||
    location?.pathname === '/email-verification-confirmation'
  ) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Welcome message */}
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 rounded-full p-2">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-sm sm:text-base">
                Welcome to MyBodyToday! 🎉
              </p>
              <p className="text-xs sm:text-sm opacity-90">
                Start your health journey by logging your first daily check-in
              </p>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleStartCheckIn}
              className="bg-white text-teal-600 hover:bg-gray-100 px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Start Check-In</span>
              <span className="sm:hidden">Start</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleDismiss}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstCheckInBanner;